import { z } from 'zod';
import { FilterFieldMetaSchema } from '../common/filters.js';

/**
 * Ticket response shapes.
 *
 * Defined here rather than in either application so the API and the web client
 * are typed from one definition and cannot drift (ADR-005). The API builds
 * these; the client consumes them; TypeScript enforces that they agree.
 */

// ---------------------------------------------------------------------------
// Reference shapes
// ---------------------------------------------------------------------------

export const StatusRefSchema = z.object({
  key: z.string(),
  label: z.string(),
  /**
   * The wording a requester sees. Statuses have always stored one —
   * "Awaiting your reply" where the desk says "Pending" — and nothing read it,
   * so requesters saw the desk's internal vocabulary. Present on tickets.
   */
  requester_label: z.string().optional(),
  /** The semantic bucket automation and reporting key off. */
  category: z.enum(['open', 'pending', 'resolved', 'closed']),
  colour: z.string().nullable(),
});
export type StatusRef = z.infer<typeof StatusRefSchema>;

export const PriorityRefSchema = z.object({
  key: z.string(),
  label: z.string(),
  /** Ordinal weight. Sorting uses this, never the label. */
  level: z.number().int(),
  colour: z.string().nullable(),
});
export type PriorityRef = z.infer<typeof PriorityRefSchema>;

export const PersonRefSchema = z.object({
  id: z.number().int(),
  name: z.string(),
});
export type PersonRef = z.infer<typeof PersonRefSchema>;

export const RequesterRefSchema = PersonRefSchema.extend({
  email: z.string(),
  is_vip: z.boolean(),
});
export type RequesterRef = z.infer<typeof RequesterRefSchema>;

export const NamedRefSchema = z.object({ id: z.number().int(), name: z.string() });
export type NamedRef = z.infer<typeof NamedRefSchema>;

// ---------------------------------------------------------------------------
// SLA
// ---------------------------------------------------------------------------

export const SLA_STATES = ['none', 'on_track', 'at_risk', 'breached', 'paused', 'met'] as const;
export const SlaStateSchema = z.enum(SLA_STATES);
export type SlaState = z.infer<typeof SlaStateSchema>;

/** Display metadata for each SLA state, so the API and UI agree on wording. */
export const SLA_STATE_LABELS: Record<SlaState, string> = {
  none: 'No SLA',
  on_track: 'On track',
  at_risk: 'At risk',
  breached: 'Breached',
  paused: 'Paused',
  met: 'Met',
};

// ---------------------------------------------------------------------------
// Ticket
// ---------------------------------------------------------------------------

export const TicketSummarySchema = z.object({
  id: z.number().int(),
  /** The number people quote. Distinct from the internal id. */
  number: z.number().int(),
  workspace_id: z.number().int(),
  subject: z.string(),

  status: StatusRefSchema,
  priority: PriorityRefSchema,
  type: z.string().nullable(),
  /** How the type reads ("Service request"), where `type` is its key. */
  type_label: z.string().nullable().optional(),
  source: z.string().nullable(),
  /** How the source reads ("Walk-up"), where `source` is its key. */
  source_label: z.string().nullable().optional(),
  /**
   * How soon it hurts and how widely: 1 low, 2 medium, 3 high. The priority
   * matrix works the priority out from the pair when either is set without a
   * priority. Sent to the desk only — a requester never sets or sees them, so
   * optional here.
   */
  urgency: z.number().int().min(1).max(3).optional(),
  impact: z.number().int().min(1).max(3).optional(),
  /**
   * The optimistic concurrency token. Send it back with an edit and the server
   * refuses the edit if somebody else changed the ticket in between, rather
   * than letting the second write silently undo the first.
   */
  version: z.number().int().optional(),
  /** Where this ticket's conversation went, when it was merged into another. */
  merged_into: z.object({ number: z.number().int() }).nullable().optional(),

  requester: RequesterRefSchema.nullable(),
  assignee: PersonRefSchema.nullable(),
  group: NamedRefSchema.nullable(),
  department: NamedRefSchema.nullable(),

  sla_state: SlaStateSchema,
  /**
   * When the desk first replied. The first-response due date stays on the
   * ticket after it is met, so without this a screen could not tell "met at
   * 10:05" from "overdue since 10:00" — and every list showed the first as
   * the second.
   */
  first_responded_at: z.string().nullable(),
  /** The nearest deadline still running, and which target it belongs to. */
  sla_due_at: z.string().nullable(),
  sla_due_target: z.enum(['first_response', 'next_response', 'resolution']).nullable(),
  first_response_due_at: z.string().nullable(),
  /** Null unless the requester is waiting on an answer right now. */
  next_response_due_at: z.string().nullable(),
  resolution_due_at: z.string().nullable(),
  is_escalated: z.boolean(),

  created_at: z.string(),
  updated_at: z.string(),
  last_activity_at: z.string(),
  resolved_at: z.string().nullable(),

  tags: z.array(z.string()),
  custom_fields: z.record(z.string(), z.unknown()),
});
export type TicketSummary = z.infer<typeof TicketSummarySchema>;

/**
 * One SLA target on one ticket, in its own state.
 *
 * The ticket's due-date columns survive a target being met or paused, so they
 * cannot say on their own whether "10:00" is a deadline or history.
 */
export const SlaTimerSummarySchema = z.object({
  target_type: z.enum(['first_response', 'next_response', 'resolution']),
  state: z.enum(['running', 'paused', 'met', 'breached']),
  due_at: z.string().nullable(),
  met_at: z.string().nullable(),
  breached_at: z.string().nullable(),
  paused_at: z.string().nullable(),
  /** Running and past its warning point. */
  at_risk: z.boolean(),
});
export type SlaTimerSummary = z.infer<typeof SlaTimerSummarySchema>;

export const TicketDetailSchema = TicketSummarySchema.extend({
  description_html: z.string(),
  description_text: z.string(),
  /** Every SLA target, for the desk. Always empty for a requester. */
  sla_timers: z.array(SlaTimerSummarySchema).optional(),
});
export type TicketDetail = z.infer<typeof TicketDetailSchema>;

// ---------------------------------------------------------------------------
// Timeline
// ---------------------------------------------------------------------------

export const TIMELINE_MESSAGE_KINDS = ['reply', 'note', 'forward', 'description'] as const;
export type TimelineMessageKind = (typeof TIMELINE_MESSAGE_KINDS)[number];

export interface TimelineMessage {
  entry_type: 'message';
  id: number;
  kind: TimelineMessageKind;
  body_html: string;
  body_text: string;
  created_at: string;
  author_id: number | null;
  author_type: string;
  author_name: string | null;
  author_email: string | null;
  source: string;
}

export interface TimelineEvent {
  entry_type: 'event';
  id: number;
  activity_type: string;
  /** `label` names a custom field, which `field` holds as `custom_fields.<key>`. */
  changes: Array<{ field: string; from: unknown; to: unknown; label?: string }>;
  metadata: Record<string, unknown>;
  created_at: string;
  actor_id: number | null;
  actor_type: string;
  actor_name: string | null;
}

export type TimelineEntry = TimelineMessage | TimelineEvent;

export function isTimelineMessage(entry: TimelineEntry): entry is TimelineMessage {
  return entry.entry_type === 'message';
}

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

export const SessionUserSchema = z.object({
  id: z.number().int(),
  email: z.string(),
  name: z.string(),
  job_title: z.string().nullable(),
  timezone: z.string(),
  language: z.string(),
  is_vip: z.boolean(),
  department_id: z.number().int().nullable(),
  location_id: z.number().int().nullable(),
});
export type SessionUser = z.infer<typeof SessionUserSchema>;

export const SessionSchema = z.object({
  user: SessionUserSchema,
  is_agent: z.boolean(),
  /** Present while an administrator is acting as this user. Never hidden. */
  impersonated_by: z.number().int().nullable(),
  workspaces: z.array(
    z.object({
      id: z.number().int(),
      key: z.string(),
      name: z.string(),
      is_default: z.boolean(),
    }),
  ),
  groups: z.array(
    z.object({ id: z.number().int(), name: z.string(), workspace_id: z.number().int() }),
  ),
  permissions: z.array(
    z.object({
      permission: z.string(),
      scope: z.string(),
      workspaceId: z.number().int().nullable(),
    }),
  ),
  mfa_satisfied: z.boolean(),
});
export type Session = z.infer<typeof SessionSchema>;

// ---------------------------------------------------------------------------
// Filter metadata
// ---------------------------------------------------------------------------

export const TicketFilterMetaSchema = z.object({
  resource: z.literal('tickets'),
  fields: z.array(FilterFieldMetaSchema),
});
export type TicketFilterMeta = z.infer<typeof TicketFilterMetaSchema>;
